<template>
  <div
    v-if="typeof localValue === 'string'"
    :class="$style.base"
  >
    <div :class="$style.form">
      <PageSplit :width="40">
        <template #left>
          <BaseInputLabel :text="t('promote.booking_widget.settings.color')" />
          <BaseColorLabel
            v-model="localValue"
            colorPicker
            selected
            mb
            v-test="'booking-settings-wizard-customColor'"
          />
          <BaseInputLabel :text="t('promote.booking_widget.link')" />
          <BaseCodeField
            :code="widgetUrl"
            :loading="loadingMicroSite"
            buttonColor="inverted"
            isUrl
            multiline
            v-test="'widget-styling-link'"
          />
        </template>
        <template #right>
          <div :class="$style.widget">
            <iframe
              ref="widgetPreviewIframe"
              :key="iframeUpdate"
              :class="$style.widgetPreview"
              :src="widgetPreviewUrl"
              v-test="'booking-settings-preview-iframe'"
            />
          </div>
        </template>
      </PageSplit>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import PageSplit from '@/modules/PageSplit.vue';
import { useCompanyStore } from '@/stores/company';
import { useMicroSiteStore } from '@/stores/micro-site';

const { bookingWidgetUrl: widgetUrl, loading: loadingMicroSite } = storeToRefs(
  useMicroSiteStore(),
);

const { company } = useCompanyStore();
const { t } = useI18n();

const emit = defineEmits(['update:modelValue']);

const props = defineProps<{
  modelValue?: string | null;
}>();

const localValue = ref(props.modelValue);

watch(
  localValue,
  () => {
    emit('update:modelValue', localValue.value);
  },
  {
    deep: true,
  },
);

const widgetPreviewUrl = computed(() => {
  let baseUrl = `${config.bookingWidgetUrl}/?widget=booking&company=${company.publicId}`;
  baseUrl += `&language=${company.languagePublic}`;
  baseUrl += '&inline=true';
  baseUrl += `&color=${localValue.value}`;
  return encodeURI(baseUrl);
});

const widgetPreviewIframe = ref<HTMLIFrameElement | null>(null);
const iframeUpdate = ref(0);

const refreshIframe = () => {
  if (widgetPreviewIframe.value) {
    iframeUpdate.value += 1;
  }
};

watch(widgetPreviewUrl, () => {
  refreshIframe();
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.form {
  flex-grow: 1;
}
.widget {
  position: relative;
  background-color: var(--color-bg-secondary);
  border-left: 1px solid var(--color-border);
  overflow: hidden;
  height: 100%;
  min-height: 600px;

  iframe {
    border: 0;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1; // To place it over the text message
  }
}
</style>
